/* Udyan B2B - enquiry form field diagnostics.

   Built at the CMO session's request (handover 10 Sep, §6). The form ships at
   5 required fields on a judgement about B2B buyer intent. This is what turns
   that judgement into a measured fact: which field, if any, actually costs
   submissions. Without it the 7-day watch says the rate moved and nothing
   about why.

   PRIVACY, and it is not negotiable.
   These are named business contacts. This module NEVER transmits, stores or
   derives anything from what a visitor types. The only values that reach
   send() are: a slug from the fixed FIELDS map below, an integer ordinal, a
   duration in seconds, and booleans. A field's contents are read in exactly
   one place, isFilled(), and only ever collapse to true/false.

   If you add a parameter here, it must come from that same closed set. Never
   pass el.value, and never add a map entry for the honeypot.

   EVENT NAMES
   b2b_form_start, b2b_field_complete, b2b_field_invalid, b2b_form_submit,
   b2b_form_abandon

   All diagnostics. None of them may be marked a GA4 key event, and none can
   collide with the conversion rule, which matches event_name EQUALS
   'b2b_enquiry_submitted' exactly. Keep the b2b_form_ / b2b_field_ prefixes.

   b2b_field_invalid is the one that answers the question: it fires when the
   browser blocks submission, naming the field that blocked it. */

(function () {
  'use strict';

  var FIELDS = {
    'contact[Full name]': 'name',
    'contact[Company]': 'company',
    'contact[email]': 'email',
    'contact[Country code]': 'phone_code',
    'contact[Phone]': 'phone',
    'contact[What you need]': 'need',
    'contact[Estimated monthly volume]': 'volume',
    'contact[body]': 'message',
    'contact[Preferred reply]': 'reply'
  };
  /* Deliberately absent: contact[Website] is the spam honeypot, and the hidden
     attribution inputs are not fields a human touches. */

  var TOTAL = 0;
  for (var k in FIELDS) { if (Object.prototype.hasOwnProperty.call(FIELDS, k)) TOTAL++; }

  /* LOAD-ORDER RACE, fixed 14 Sep 2026 (CMO handover §1). gtag is injected
     asynchronously by Shopify's Google & YouTube app, so on a slow connection a
     visitor can start typing before it exists. The old fallback pushed an
     {event: ...} object into dataLayer, which gtag.js ignores and no GTM trigger
     reads - those events were simply lost.

     Now an event waits for gtag (250 ms steps, up to 10 s) and then fires. If
     gtag never arrives, or the page is already leaving, the command is queued in
     gtag's own form: a real Arguments object, which gtag.js drains from
     dataLayer when it loads. Never a plain array, never an object. */
  var GTAG_POLL_MS = 250;
  var GTAG_WAIT_MS = 10000;

  function queueForGtag(name, params) {
    window.dataLayer = window.dataLayer || [];
    (function () { window.dataLayer.push(arguments); })('event', name, params);
  }

  /* leaving: true for submit and abandon. A timer set while the page unloads
     never runs, so those queue immediately instead of waiting. */
  function send(name, params, leaving) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, params);
        return;
      }
      if (leaving) {
        queueForGtag(name, params);
        return;
      }
      var startedAt = Date.now(); /* real time: background tabs throttle timers */
      (function retry() {
        try {
          if (typeof window.gtag === 'function') {
            window.gtag('event', name, params);
          } else if (Date.now() - startedAt >= GTAG_WAIT_MS) {
            queueForGtag(name, params);
          } else {
            setTimeout(retry, GTAG_POLL_MS);
          }
        } catch (e) { /* diagnostics must never break the form */ }
      })();
    } catch (e) { /* diagnostics must never break the form */ }
  }

  function slug(el) {
    return (el && el.name && FIELDS[el.name]) || null;
  }

  /* The ONLY place field contents are read. Returns a boolean and nothing else. */
  function isFilled(el) {
    if (el.type === 'radio' || el.type === 'checkbox') {
      var group = el.form ? el.form.querySelectorAll('[name="' + el.name + '"]') : [];
      for (var i = 0; i < group.length; i++) { if (group[i].checked) return true; }
      return false;
    }
    return String(el.value == null ? '' : el.value).trim() !== '';
  }

  function reason(el) {
    var v = el.validity;
    if (!v) return 'unknown';
    if (v.valueMissing) return 'empty';
    if (v.typeMismatch) return 'wrong_format';
    if (v.patternMismatch) return 'pattern';
    if (v.tooShort) return 'too_short';
    if (v.tooLong) return 'too_long';
    return 'other';
  }

  function secondsSince(ms) {
    return Math.min(600, Math.round((Date.now() - ms) / 1000));
  }

  function init(form) {
    var started = false;
    var submitted = false;
    var startedAt = 0;
    var focusedAt = {};
    var completed = {};
    var completedCount = 0;
    var lastField = '';
    var order = 0;

    function markComplete(el) {
      var s = slug(el);
      if (!s || completed[s] || !isFilled(el)) return;
      completed[s] = true;
      completedCount++;
      order++;
      send('b2b_field_complete', {
        field: s,
        order: order,
        seconds: focusedAt[s] ? secondsSince(focusedAt[s]) : 0
      });
    }

    form.addEventListener('focusin', function (e) {
      var s = slug(e.target);
      if (!s) return;
      lastField = s;
      focusedAt[s] = Date.now();
      if (!started) {
        started = true;
        startedAt = Date.now();
        send('b2b_form_start', { field: s });
      }
    });

    form.addEventListener('focusout', function (e) {
      if (slug(e.target)) markComplete(e.target);
    });

    /* The custom dropdown moves focus to its own trigger button, so a <select>
       may never see focusout. It dispatches a bubbling 'change' on the real
       select instead — see b2b-select.js — which is what catches it here.
       Radio chips land here too. */
    form.addEventListener('change', function (e) {
      var s = slug(e.target);
      if (!s) return;
      lastField = s;
      if (!started) {
        started = true;
        startedAt = Date.now();
        send('b2b_form_start', { field: s });
      }
      markComplete(e.target);
    });

    /* 'invalid' does not bubble — capture phase is required. */
    form.addEventListener('invalid', function (e) {
      var s = slug(e.target);
      if (!s) return;
      send('b2b_field_invalid', { field: s, reason: reason(e.target) });
    }, true);

    form.addEventListener('submit', function () {
      submitted = true;
      send('b2b_form_submit', {
        fields_completed: completedCount,
        fields_total: TOTAL,
        seconds: started ? secondsSince(startedAt) : 0
      }, true);
    });

    function abandon() {
      if (!started || submitted) return;
      submitted = true; /* fire once, whichever handler gets there first */
      send('b2b_form_abandon', {
        last_field: lastField,
        fields_completed: completedCount,
        fields_total: TOTAL,
        seconds: secondsSince(startedAt)
      }, true);
    }

    /* pagehide is the reliable one on mobile Safari; visibilitychange covers
       the tab-switch-then-never-return case that pagehide misses. */
    window.addEventListener('pagehide', abandon);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') abandon();
    });
  }

  function boot() {
    var forms = document.querySelectorAll('form.b2b-form, .b2b-form-card form');
    for (var i = 0; i < forms.length; i++) { init(forms[i]); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
